import {
  currentClassRoomId,
  isInstructor,
  accessingSpaceAsStaff,
} from '../selectors/classroom';

const materialApp = (state) => state.materials;
const materialData = (state) => state.data.materials;

export const currentSectionId = (state) => {
  const { routing } = state;
  if (routing) {
    const {
      locationBeforeTransitions: { pathname },
    } = routing;
    const materialComponentIndex = pathname.search('/materials/');
    if (materialComponentIndex !== -1) {
      return pathname
        .substring(materialComponentIndex)
        .replace('/materials/', '');
    }

    const educatorMaterialComponentIndex = pathname.search('/educatorArea/');
    if (educatorMaterialComponentIndex !== -1) {
      return pathname
        .substring(educatorMaterialComponentIndex)
        .replace('/educatorArea/', '');
    }
  }

  // eslint-disable-next-line consistent-return
  return undefined;
};

export const isDeletingSection = (state) =>
  materialApp(state).isDeletingSection;
export const editing = (state) => materialApp(state).editing;
export const isFetchingSections = (state) =>
  materialApp(state).isFetchingSections;
export const isLoadingContent = (state) => materialApp(state).isLoadingContent;
export const isHidingMaterial = (state) => materialApp(state).isHidingMaterial;
export const isFetchingVersions = (state) =>
  materialApp(state).isFetchingVersions;
export const isLoadingVersion = (state) => materialApp(state).isLoadingVersion;
export const isErrored = (state) => materialApp(state).isErrored;
export const isRevertingSection = (state) =>
  materialApp(state).isRevertingSection;
export const isSavingSection = (state) => materialApp(state).isSavingSection;
export const isImportingMaterials = (state) =>
  materialApp(state).isImportingMaterials;
export const currentDeleteCandidate = (state) =>
  materialApp(state).currentDeleteCandidate;
export const currentDeleteCandidateCheckNull = (state) =>
  currentDeleteCandidate(state) !== null;
export const selectedContent = (state) => materialApp(state).selectedContent;
export const selectedVersion = (state) => materialApp(state).selectedVersion;
export const leaveDestinationPath = (state) =>
  materialApp(state).leaveDestinationPath;
export const hasDismissedGetStarted = (state) =>
  materialApp(state).hasDismissedGetStarted;
export const currentRevertCandidate = (state) =>
  materialData(state)[materialApp(state).currentRevertCandidate];
export const hasFetchedSections = (state) =>
  materialApp(state).hasFetchedSections;
export const shouldFetchSections = (state) =>
  !(hasFetchedSections(state) || isFetchingSections(state) || isErrored(state));
export const isExpanded = (state, folderId) =>
  !!materialApp(state).expandedFolders[folderId];
export const isMaterialEducatorOnlyExpanded = (state) =>
  materialApp(state).isMaterialEducatorOnlyExpanded;
export const renameFolderDialogOpenFor = (state) =>
  materialApp(state).renameFolderDialogOpenFor;
export const deleteFolderDialogOpenFor = (state) =>
  materialApp(state).deleteFolderDialogOpenFor;
export const scheduleMaterialDialogOpenFor = (state) =>
  materialApp(state).scheduleMaterialDialogOpenFor;

export const scheduleSectionDialogOpenFor = (state) =>
  materialApp(state).scheduleSectionDialogOpenFor;

export const isSavingFolder = (state) => materialApp(state).isSavingFolder;
export const isDeletingFolder = (state) => materialApp(state).isDeletingFolder;

export const getMaterial = (state, materialId) => {
  if (typeof materialId === 'undefined') return null;
  return materialData(state)[materialId];
};

export const isFolder = (state, materialId) => {
  const material = getMaterial(state, materialId);
  return !!(material && material.isFolder);
};

export const currentSection = (state) =>
  getMaterial(state, currentSectionId(state));

export const currentSectionTitle = (state) => {
  const s = currentSection(state);
  return s ? s.title : '';
};

const compareMaterials = (a, b) => {
  if (a.order < b.order) return -1;
  if (a.order > b.order) return 1;
  return 0;
};

export const sectionsByParent = (state, parent) => {
  const spaceId = currentClassRoomId(state);
  const materials = materialData(state);
  const parentMaterial = getMaterial(state, parent);
  const currentUserIsInstructor = isInstructor(state);
  const userIsStaff = accessingSpaceAsStaff(state)(spaceId);
  return Object.keys(materials)
    .map((id) => materials[id])
    .filter((material) => {
      const matchSpace = material.space === spaceId;
      const matchParent = parent
        ? material.parent === parent
        : !material.parent;
      const parentIsHidden = parentMaterial && parentMaterial.hidden;
      const materialIsHidden = material.hidden || parentIsHidden;
      const visible =
        currentUserIsInstructor || userIsStaff || !materialIsHidden;

      return matchSpace && matchParent && visible;
    })
    .sort(compareMaterials);
};

export const sectionIdsByParent = (state, parent, educatorOnly) =>
  sectionsByParent(state, parent)
    .filter((material) => {
      if (educatorOnly) {
        return material.educatorOnly;
      }
      return !material.educatorOnly;
    })
    .map((material) => material.id);

export const topLevelMaterials = (state) => sectionsByParent(state, undefined);
export const topLevelMaterialIds = (state) =>
  sectionIdsByParent(state, undefined);
export const topLevelMaterialEducatorOnlyIds = (state) =>
  sectionIdsByParent(state, undefined, true);

export const getFirstMaterialId = (state, { educatorOnly = false } = {}) => {
  const materials = materialData(state);
  if (!materials) {
    return null;
  }

  const sectionsByParentRes = sectionsByParent(state, undefined).filter(
    (section) => Boolean(section.educatorOnly) === Boolean(educatorOnly)
  );
  if (sectionsByParentRes.length === 0) {
    return null;
  }

  const firstItem = sectionsByParentRes[0];
  if (!firstItem.isFolder) {
    return firstItem.id;
  }

  const sectionsInFolder = Object.keys(materials)
    .map((id) => materials[id])
    .filter(
      (section) => Boolean(section.educatorOnly) === Boolean(educatorOnly)
    )
    .filter(({ parent }) => parent === firstItem.id)
    .sort(compareMaterials);

  if (sectionsInFolder.length === 0) {
    return null;
  }

  return sectionsInFolder[0].id;
};

export const getFirstEducatorMaterialId = (state) =>
  getFirstMaterialId(state, { educatorOnly: true });

export const hasAtLeastOneChildSection = (state) => {
  const materials = materialData(state);
  const sections = Object.keys(materials).filter(
    (id) => !materials[id].isFolder
  );

  return sections.length > 0;
};

export const fallbackSectionId = (state, materialId, educatorOnly = false) => {
  const current = currentSection(state);

  if (!current) {
    return null;
  }

  if (current.id !== materialId && current.parent !== materialId) {
    return current.id;
  }

  const material = getMaterial(state, materialId);
  const materials = sectionsByParent(state, material.parent).filter(
    (section) => Boolean(section.educatorOnly) === Boolean(educatorOnly)
  );
  const materialIdx = materials.findIndex((m) => m.id === materialId);
  const notFolder = (m) => !m.isFolder;

  const following = materials.slice(materialIdx + 1).find(notFolder);
  if (following) {
    return following.id;
  }

  const preceding = materials.slice(0, materialIdx).reverse().find(notFolder);
  if (preceding) {
    return preceding.id;
  }
  return getFirstMaterialId(state, { educatorOnly });
};
